import { Box, Button, Card, Skeleton, Typography } from "@mui/material";
import { BubbleMenu, useEditor } from "@tiptap/react";
import { useRouter } from "next/router";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { AddPaymentModal } from "src/components/dashboard/addPaymenModal";
import { RegenerateDropdown } from "src/components/dashboard/brief/rightSection/aiArticle/regenerateDropdown";
import MenuBar from "src/components/dashboard/editor/MenuBar";
import { MagicWandIcon } from "src/icons/magic-wand-icon";
import { getActivePlan } from "src/slices/auth/thunks";

import Head from "next/head";
import {
  selectIsHumanized,
  setIsHumanized,
  setShowHumanizeContent,
} from "src/slices/humanize";
import { endHumanization } from "src/slices/humanize/thunks";
import { aiWordsUpgradeModalShowed, snackAdded } from "src/slices/system";
import { useDispatch, useSelector } from "src/store";
import { copyHtmlToClipboard, truncateContent, insertUnlockCardWithTipTap } from "src/utils/clipboardUtils";
import { Export2Docx } from "src/utils/docs";
import {
  addMetaToArticle,
  countSelectedWords,
  extractSelectedBlocks,
  getEditorExtensions,
  getMarkdownConverter,
  SelectedTextItem,
  setupTurndownService,
  useDualDebounce,
  getWordCount,
} from "src/utils/editorUtils";
import Editor from "../editor";
import { FactCheckDecorationRange } from "../editor/extensions/FactCheckDecorations";
import { Onboarding } from "../onboarding";
import { OptimizeContentDialog } from "../optimizeContent/optimizeContentDialog";
import { FactCheckFinding } from "./factCheckSidebar";
import Sidebar from "./sidebar";

// Props for the ArticleEditorComponent
interface ArticleEditorComponentProps {
  id: string;
  title?: string;
  description?: string;
  isOptimizedMode?: boolean;
  hideArticle?: boolean;
  fetchContent: (id: string) => Promise<any>;
  saveContent: (content: string, options?: any) => Promise<any>;
  isDownloadContent?: boolean;
  isCopyContent?: boolean;
  additionalProps?: any;
  isEEATMode?: boolean;
}

const buildNormalizedTextMap = (input: string) => {
  let normalized = "";
  const normalizedToRaw: number[] = [];
  let previousWasSpace = true;

  for (let i = 0; i < input.length; i += 1) {
    const char = input[i];
    const isSpace = /\s/.test(char);

    if (isSpace) {
      if (!previousWasSpace) {
        normalized += " ";
        normalizedToRaw.push(i);
        previousWasSpace = true;
      }
      continue;
    }

    normalized += char.toLowerCase();
    normalizedToRaw.push(i);
    previousWasSpace = false;
  }

  if (normalized.endsWith(" ")) {
    normalized = normalized.slice(0, -1);
    normalizedToRaw.pop();
  }

  return { normalized, normalizedToRaw };
};

const unwrapDynamoValue = (value: any): any => {
  if (value == null || typeof value !== "object") return value;
  if ("S" in value) return value.S;
  if ("N" in value) return Number(value.N);
  if ("BOOL" in value) return Boolean(value.BOOL);
  if ("NULL" in value) return null;
  if ("M" in value) {
    const out: Record<string, any> = {};
    Object.entries(value.M || {}).forEach(([k, v]) => {
      out[k] = unwrapDynamoValue(v);
    });
    return out;
  }
  if ("L" in value) {
    return (value.L || []).map((item: any) => unwrapDynamoValue(item));
  }
  return value;
};

/** Normalize unicode characters that commonly differ between AI output and
 *  rich-text editors: smart quotes, em/en dashes. All replacements are
 *  1-to-1 character length to preserve offset mapping when applied to both
 *  needle and haystack. */
const normalizeUnicode = (s: string): string =>
  s
    .replace(/[\u2018\u2019\u201A\u201B]/g, "'")   // smart single quotes → '
    .replace(/[\u201C\u201D\u201E\u201F]/g, '"')   // smart double quotes → "
    .replace(/[\u2013\u2014]/g, "-");               // en-dash / em-dash → -

const buildTextSegments = (editor: any) => {
  const segments: Array<{ start: number; end: number; pos: number; text: string }> = [];
  let cursor = 0;
  let lastBlockEnd = -1;

  editor.state.doc.descendants((node: any, pos: number) => {
    // Insert newline separator between block-level nodes so cross-paragraph
    // text matching behaves correctly (matches markdown newline boundaries).
    if (node.isBlock && !node.isLeaf && segments.length > 0 && pos > lastBlockEnd) {
      segments.push({ start: cursor, end: cursor + 1, pos, text: "\n" });
      cursor += 1;
    }
    if (node.isBlock) {
      lastBlockEnd = pos + node.nodeSize;
    }
    if (!node.isText || !node.text) return;
    const text = String(node.text);
    const start = cursor;
    const end = start + text.length;
    segments.push({ start, end, pos, text });
    cursor = end;
  });
  return { segments, combinedText: segments.map((s) => s.text).join("") };
};

const charIndexToPos = (
  segments: Array<{ start: number; end: number; pos: number }>,
  index: number
) => {
  for (const segment of segments) {
    if (index >= segment.start && index <= segment.end) {
      const offset = Math.max(0, Math.min(index - segment.start, segment.end - segment.start));
      return segment.pos + offset;
    }
  }
  return null;
};

/** Check whether two strings are close enough to be considered the same text
 *  after minor whitespace / punctuation drift. */
const isCloseMatch = (a: string, b: string): boolean => {
  const na = a.toLowerCase().replace(/\s+/g, " ").trim();
  const nb = b.toLowerCase().replace(/\s+/g, " ").trim();
  if (na === nb) return true;
  if (na.length > 0 && nb.length > 0) {
    const shorter = Math.min(na.length, nb.length);
    const longer = Math.max(na.length, nb.length);
    // Allow up to 20% length difference and check prefix containment
    if (shorter / longer > 0.8 && na.includes(nb.slice(0, shorter))) return true;
  }
  return false;
};

/** Build a multiset of character bigrams for Dice coefficient scoring. */
const buildBigrams = (s: string): Map<string, number> => {
  const map = new Map<string, number>();
  const lower = s.toLowerCase();
  for (let i = 0; i < lower.length - 1; i++) {
    const bigram = lower.slice(i, i + 2);
    map.set(bigram, (map.get(bigram) || 0) + 1);
  }
  return map;
};

/** Dice coefficient between two strings (0–1). */
const bigramDice = (a: string, b: string): number => {
  if (a.length < 2 || b.length < 2) return a.toLowerCase() === b.toLowerCase() ? 1 : 0;
  const bigramsA = buildBigrams(a);
  const bigramsB = buildBigrams(b);
  let intersection = 0;
  for (const [bigram, countA] of bigramsA) {
    const countB = bigramsB.get(bigram) || 0;
    intersection += Math.min(countA, countB);
  }
  const totalA = a.length - 1;
  const totalB = b.length - 1;
  return (2 * intersection) / (totalA + totalB);
};

const findRangeForFinding = (
  editor: any,
  finding: FactCheckFinding
): FactCheckDecorationRange | null => {
  const needle = normalizeUnicode((finding.original_text || "").trim());
  if (!needle) return null;

  const { segments, combinedText: rawCombinedText } = buildTextSegments(editor);
  if (!rawCombinedText) return null;
  // Normalize unicode in haystack too (1-to-1 replacements preserve offsets).
  const combinedText = normalizeUnicode(rawCombinedText);

  // ── Strategy 1: Locator offsets ──────────────────────────────────────
  // Backend computes start_char/end_char against plain text (same space as
  // ProseMirror text nodes). Validate the text at the offset before trusting it.
  const locator = finding.locator;
  // Coerce to numbers — DynamoDB / JSON may return strings like "1804"
  const rawStartChar = locator?.start_char != null ? Number(locator.start_char) : NaN;
  const rawEndChar = locator?.end_char != null ? Number(locator.end_char) : NaN;
  if (Number.isFinite(rawStartChar) && Number.isFinite(rawEndChar) && rawStartChar < rawEndChar) {
    const startChar = rawStartChar;
    const endChar = rawEndChar;
    const textAtOffset = combinedText.slice(startChar, endChar);
    if (textAtOffset && isCloseMatch(textAtOffset, needle)) {
      const from = charIndexToPos(segments, startChar);
      const to = charIndexToPos(segments, endChar);
      if (from && to && from < to) {
        return { from, to, severity: finding.severity, findingId: finding.id };
      }
    }
  }

  // ── Strategy 2: Exact text match ─────────────────────────────────────
  const directIndex = combinedText.toLowerCase().indexOf(needle.toLowerCase());
  if (directIndex >= 0) {
    const from = charIndexToPos(segments, directIndex);
    const to = charIndexToPos(segments, directIndex + needle.length);
    if (from && to && from < to) {
      return { from, to, severity: finding.severity, findingId: finding.id };
    }
  }

  // Normalized whitespace fallback
  const normalizedHaystack = buildNormalizedTextMap(combinedText);
  const normalizedNeedle = buildNormalizedTextMap(needle).normalized;
  if (normalizedNeedle) {
    const normalizedIndex = normalizedHaystack.normalized.indexOf(normalizedNeedle);
    if (normalizedIndex >= 0) {
      const rawStart = normalizedHaystack.normalizedToRaw[normalizedIndex];
      const rawEndMapIndex = normalizedIndex + normalizedNeedle.length - 1;
      const rawEndCharIndex = normalizedHaystack.normalizedToRaw[rawEndMapIndex];
      if (Number.isFinite(rawStart) && Number.isFinite(rawEndCharIndex)) {
        const from = charIndexToPos(segments, rawStart);
        const to = charIndexToPos(segments, rawEndCharIndex + 1);
        if (from && to && from < to) {
          return { from, to, severity: finding.severity, findingId: finding.id };
        }
      }
    }
  }

  // ── Strategy 2.5: Head-tail anchor matching ─────────────────────────
  // When Claude slightly misquotes text (missing/extra words), split the
  // needle into head and tail portions and find the spanning region.
  const words = needle.split(/\s+/);
  if (words.length >= 3) {
    const midpoint = Math.ceil(words.length / 2);
    const headPhrase = words.slice(0, midpoint).join(" ");
    const tailPhrase = words.slice(-Math.max(1, Math.floor(words.length / 3))).join(" ");

    const headIdx = combinedText.toLowerCase().indexOf(headPhrase.toLowerCase());
    if (headIdx >= 0) {
      const searchAfter = headIdx + headPhrase.length;
      const tailWindow = combinedText.slice(searchAfter, searchAfter + needle.length * 2);
      const tailIdx = tailWindow.toLowerCase().indexOf(tailPhrase.toLowerCase());
      if (tailIdx >= 0) {
        const matchEnd = searchAfter + tailIdx + tailPhrase.length;
        const from = charIndexToPos(segments, headIdx);
        const to = charIndexToPos(segments, matchEnd);
        if (from && to && from < to) {
          return { from, to, severity: finding.severity, findingId: finding.id };
        }
      }
    }
  }

  // ── Strategy 3: Context-anchored search ──────────────────────────────
  // Use context_before to locate the approximate region, then search for
  // the needle nearby. Try full context first, then shorter distinctive chunks.
  if (locator?.context_before) {
    const ctxBefore = locator.context_before.trim();
    const chunks = [ctxBefore];
    // Extract last clause after a delimiter (handles markdown list → block rendering mismatch)
    const lastDelim = Math.max(ctxBefore.lastIndexOf(","), ctxBefore.lastIndexOf("."), ctxBefore.lastIndexOf(";"));
    if (lastDelim > 0 && ctxBefore.length - lastDelim > 5) {
      chunks.push(ctxBefore.slice(lastDelim + 1).trim());
    }
    if (ctxBefore.length > 20) {
      chunks.push(ctxBefore.slice(-20).trim());
    }

    for (const chunk of chunks) {
      if (chunk.length < 5) continue;
      const ctxIndex = combinedText.toLowerCase().indexOf(chunk.toLowerCase());
      if (ctxIndex < 0) continue;
      const searchStart = ctxIndex + chunk.length;
      const searchWindow = combinedText.slice(searchStart, searchStart + needle.length * 2 + 100);

      // Try exact needle match in window
      const needleInWindow = searchWindow.toLowerCase().indexOf(needle.toLowerCase());
      if (needleInWindow >= 0) {
        const absoluteStart = searchStart + needleInWindow;
        const from = charIndexToPos(segments, absoluteStart);
        const to = charIndexToPos(segments, absoluteStart + needle.length);
        if (from && to && from < to) {
          return { from, to, severity: finding.severity, findingId: finding.id };
        }
      }

      // Try head-tail anchor within the context window
      if (words.length >= 3) {
        const midpoint = Math.ceil(words.length / 2);
        const headPhrase = words.slice(0, midpoint).join(" ");
        const tailPhrase = words.slice(-Math.max(1, Math.floor(words.length / 3))).join(" ");

        const headInWindow = searchWindow.toLowerCase().indexOf(headPhrase.toLowerCase());
        if (headInWindow >= 0) {
          const afterHead = headInWindow + headPhrase.length;
          const tailInWindow = searchWindow.slice(afterHead, afterHead + needle.length * 2).toLowerCase().indexOf(tailPhrase.toLowerCase());
          if (tailInWindow >= 0) {
            const absStart = searchStart + headInWindow;
            const absEnd = searchStart + afterHead + tailInWindow + tailPhrase.length;
            const from = charIndexToPos(segments, absStart);
            const to = charIndexToPos(segments, absEnd);
            if (from && to && from < to) {
              return { from, to, severity: finding.severity, findingId: finding.id };
            }
          }
        }
      }
    }
  }

  // ── Strategy 4: Bounded fuzzy match (bigram Dice) ───────────────────
  // Only search within a candidate region anchored by context or head phrase.
  // Requires ≥0.95 confidence with no ambiguous second-best.
  const FUZZY_THRESHOLD = 0.95;
  const AMBIGUITY_GAP = 0.03;

  // Determine candidate region start
  let regionStart = 0;
  let regionFound = false;

  // Try context_before to narrow region
  if (locator?.context_before) {
    const ctx = locator.context_before.trim();
    if (ctx.length >= 5) {
      const ctxIdx = combinedText.toLowerCase().indexOf(ctx.toLowerCase());
      if (ctxIdx >= 0) {
        regionStart = ctxIdx + ctx.length;
        regionFound = true;
      }
    }
    // Try last 20 chars of context_before
    if (!regionFound && ctx.length > 20) {
      const shortCtx = ctx.slice(-20).trim();
      const ctxIdx = combinedText.toLowerCase().indexOf(shortCtx.toLowerCase());
      if (ctxIdx >= 0) {
        regionStart = ctxIdx + shortCtx.length;
        regionFound = true;
      }
    }
  }

  // Try head phrase to narrow region
  if (!regionFound && words.length >= 3) {
    const headPhrase = words.slice(0, Math.ceil(words.length / 2)).join(" ");
    const headIdx = combinedText.toLowerCase().indexOf(headPhrase.toLowerCase());
    if (headIdx >= 0) {
      regionStart = headIdx;
      regionFound = true;
    }
  }

  // Run fuzzy scan over a region. Returns best match or null.
  const fuzzyScan = (
    scanStart: number, scanEnd: number, stepSize: number
  ): FactCheckDecorationRange | null => {
    const searchRegion = combinedText.slice(scanStart, scanEnd);
    const windowSize = needle.length;

    let bestScore = 0;
    let bestStart = 0;
    let bestLen = 0;
    let secondBest = 0;

    const minLen = Math.floor(windowSize * 0.8);
    const maxLen = Math.ceil(windowSize * 1.2);

    for (let wLen = minLen; wLen <= maxLen; wLen++) {
      for (let i = 0; i <= searchRegion.length - wLen; i += stepSize) {
        const candidate = searchRegion.slice(i, i + wLen);
        const score = bigramDice(needle, candidate);
        if (score > bestScore) {
          secondBest = bestScore;
          bestScore = score;
          bestStart = i;
          bestLen = wLen;
        } else if (score > secondBest) {
          secondBest = score;
        }
      }
    }

    // Refine: re-scan char-by-char around best position if step > 1
    if (stepSize > 1 && bestScore >= FUZZY_THRESHOLD * 0.9) {
      const refineStart = Math.max(0, bestStart - stepSize);
      const refineEnd = Math.min(searchRegion.length, bestStart + stepSize);
      for (let wLen = minLen; wLen <= maxLen; wLen++) {
        for (let i = refineStart; i <= Math.min(refineEnd, searchRegion.length - wLen); i++) {
          const candidate = searchRegion.slice(i, i + wLen);
          const score = bigramDice(needle, candidate);
          if (score > bestScore) {
            secondBest = bestScore;
            bestScore = score;
            bestStart = i;
            bestLen = wLen;
          } else if (score > secondBest) {
            secondBest = score;
          }
        }
      }
    }

    if (bestScore >= FUZZY_THRESHOLD && (bestScore - secondBest) >= AMBIGUITY_GAP) {
      const absStart = scanStart + bestStart;
      const absEnd = absStart + bestLen;
      const from = charIndexToPos(segments, absStart);
      const to = charIndexToPos(segments, absEnd);
      if (from && to && from < to) {
        console.debug(
          `[FactCheck] Fuzzy match for finding ${finding.id}: score=${bestScore.toFixed(3)}, ` +
          `needle=${needle.slice(0, 50)}..., matched=${combinedText.slice(absStart, absEnd).slice(0, 50)}...`
        );
        return { from, to, severity: finding.severity, findingId: finding.id };
      }
    }
    return null;
  };

  if (needle.length >= 10) {
    if (regionFound) {
      // Anchored scan: tight region, char-by-char step
      const scanEnd = Math.min(combinedText.length, regionStart + needle.length * 4);
      const result = fuzzyScan(regionStart, scanEnd, 1);
      if (result) return result;
    }

    // Fallback: scan around locator hint if available, else from start; capped at 10k chars
    const MAX_SCAN_CHARS = 10000;
    const stepSize = needle.length >= 20 ? 5 : 3;
    let fallbackStart = 0;
    if (Number.isFinite(rawStartChar) && rawStartChar > 0) {
      fallbackStart = Math.max(0, rawStartChar - Math.floor(MAX_SCAN_CHARS / 4));
    }
    const fallbackEnd = Math.min(combinedText.length, fallbackStart + MAX_SCAN_CHARS);
    const result = fuzzyScan(fallbackStart, fallbackEnd, stepSize);
    if (result) return result;
  }

  console.debug(
    `[FactCheck] UNMATCHED finding ${finding.id}: needle=${needle.slice(0, 60)}`
  );
  return null;
};

const ArticleEditorComponent: React.FC<ArticleEditorComponentProps> = ({
  id,
  title = "Default Title",
  description = "Default Description",
  isOptimizedMode = false,
  hideArticle = false,
  fetchContent,
  saveContent,
  additionalProps = {},
  isEEATMode = false,
}) => {
  const dispatch = useDispatch();
  const isHumanized = useSelector(selectIsHumanized);
  const [showAIDropdown, setShowAIDropdown] = useState(false);
  const [selectedText, setSelectedText] = useState<SelectedTextItem[]>([]);
  const [shouldShowBubbleMenu, setShouldShowBubbleMenu] = useState(true);
  const [bottomMargin, setBottomMargin] = useState(0);
  const [showSidebar, setShowSidebar] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showOnboardingModal, setShowOnboardingModal] = useState(false);
  const [isOptimizeContentDialogOpen, setIsOptimizeContentDialogOpen] =
    useState(false);
  const [cords, setCords] = useState({
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
  });
  // Add state for selection coordinates to use in fixed positioning
  const [selectionCoords, setSelectionCoords] = useState({
    startCoords: { left: 0, right: 0, top: 0, bottom: 0 },
    endCoords: { left: 0, right: 0, top: 0, bottom: 0 },
  });
  const [html, setHtml] = useState("");
  const [humanizedHtml, setHumanizedHtml] = useState("");
  const htmlRef = useRef("");
  const humanizedHtmlRef = useRef("");
  const isContentLoaded = useRef(false);
  const [editorContent, setEditorContent] = useState<string>("");
  const [evaluationData, setEvaluationData] = useState<any>(null);
  const [isValidEvaluationData, setIsValidEvaluationData] =
    useState<boolean>(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [truncatedContent, setTruncatedContent] = useState("");
  const [isEditingHumanizedVersion, setIsEditingHumanizedVersion] =
    useState(false);
  const [isOptimizeContentDialogLoading, setIsOptimizeContentDialogLoading] =
    useState(false);
  const [articleLink, setArticleLink] = useState<string | null>(null);
  const [useScrollBasedTruncation, setUseScrollBasedTruncation] = useState(false);
  const [wordCount, setWordCount] = useState(0);
  const [isWritingMode, setIsWritingMode] = useState(false);
  const [factCheckFindings, setFactCheckFindings] = useState<FactCheckFinding[]>([]);
  const [activeFindingId, setActiveFindingId] = useState<number | null>(null);
  const converter = getMarkdownConverter();
  const router = useRouter();

  // Refs to track programmatic changes and content saving
  const isContentSaving = React.useRef(false);
  const lastSavedContent = React.useRef("");
  const isProgrammaticChange = React.useRef(false);

  // Create local ref to track which version we're currently editing (humanized or original)
  const isEditingHumanizedContent = React.useRef<boolean>(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Sequence counter to prevent out-of-order toggle writes (Fix P1)
  const toggleSequence = React.useRef<number>(0);

  // Note: Scroll detection no longer needed since unlock card is embedded in content

  // Debounce the save content function with dual channels (Humanized vs Original)
  const { debounce: debouncedSaveContent, flush: flushPendingSaves } = useDualDebounce(
    (content: string, options = {}) => handleSaveContent(content, options),
    1000
  );
  const turndownService = setupTurndownService();
  const editor = useEditor(
    {
      extensions: getEditorExtensions(),
      onCreate({ editor }) {
        editor.view.dom.addEventListener(
          "paste",
          (event) => {
            if (document.activeElement?.closest(".MuiTooltip-popper")) {
              event.stopPropagation();
            }
          },
          true
        );
        lastSavedContent.current = editor.getHTML();
        // Initialize word count
        setWordCount(getWordCount(editor.getText()));
      },
      content: editorContent,
      onUpdate: ({ editor }) => {
        if (isProgrammaticChange.current) {
          return;
        }
        const currentContent = editor.getHTML();

        // Update word count
        setWordCount(getWordCount(editor.getText()));

        if (currentContent !== lastSavedContent.current) {
          // Use our local ref to track which version we're saving
          const saveOptions = {
            isEdited: true,
            isHumanized: isEditingHumanizedContent.current ?? false,
          };

          // Debounce the save operation to avoid excessive API calls
          debouncedSaveContent(currentContent, saveOptions);
          // Don't update lastSavedContent here - wait for successful save in handleSaveContent
        }
      },
    },
    [editorContent]
  );

  // Function to load content
  const getNormalizedFactCheckFindings = (data: any): FactCheckFinding[] => {
    const parseIfJson = (value: any) => {
      if (typeof value !== "string") return value;
      try {
        return JSON.parse(value);
      } catch {
        return value;
      }
    };

    const normalizedData = unwrapDynamoValue(data);
    const candidates = [
      normalizedData?.factCheckFindings,
      normalizedData?.fact_check_findings,
      normalizedData?.factCheck?.findings,
      normalizedData?.fact_check?.findings,
      normalizedData?.factCheckResult?.findings,
      normalizedData?.fact_check_result?.findings,
      normalizedData?.factCheck,
      normalizedData?.fact_check,
    ];

    const rawFindings = candidates
      .map(parseIfJson)
      .map(unwrapDynamoValue)
      .find((entry) => Array.isArray(entry));

    if (!Array.isArray(rawFindings)) {
      return [];
    }

    return rawFindings
      .filter((item) => item && typeof item === "object")
      .map((item: any, index: number): FactCheckFinding => ({
        id: Number.isFinite(Number(item.id)) ? Number(item.id) : index + 1,
        section_heading: String(item.section_heading ?? item.sectionHeading ?? "Introduction"),
        category: String(item.category ?? "unverifiable_claim"),
        severity: String(item.severity ?? "warning"),
        original_text: String(item.original_text ?? item.originalText ?? ""),
        replacement_text:
          item.replacement_text === null || item.replacementText === null
            ? null
            : String(item.replacement_text ?? item.replacementText ?? ""),
        reasoning: String(item.reasoning ?? ""),
        source: String(item.source ?? ""),
        action: String(item.action ?? "flag_for_review"),
        locator: item.locator,
      }))
      .filter((finding) => finding.original_text.trim().length > 0);
  };

  const loadContent = async () => {
    try {
      // Reset loaded flag to prevent effects from running with stale refs during fetch
      isContentLoaded.current = false;
      setIsLoading(true);
      const data = await fetchContent(id);

      // Process the content based on the data format returned
      if (data) {
        const contentHtml = converter.makeHtml(data.content || "");
        const humanizedHtml = converter.makeHtml(data.humanizedContent || "");

        setHtml(contentHtml);
        setHumanizedHtml(humanizedHtml);
        htmlRef.current = contentHtml;
        humanizedHtmlRef.current = humanizedHtml;
        isContentLoaded.current = true;

        // Set both states based on the data
        dispatch(setIsHumanized(data.isHumanized));
        dispatch(setShowHumanizeContent(data.isHumanized));

        // Initialize editing state
        isEditingHumanizedContent.current = data.isHumanized;
        setIsEditingHumanizedVersion(data.isHumanized);

        // Set the initial content
        const initialContent = data.isHumanized ? humanizedHtml : contentHtml;

        // Initialize word count from HTML content (which will be displayed in editor)
        // Create a temporary div to extract text from HTML
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = initialContent;
        const textContent = tempDiv.textContent || tempDiv.innerText || '';
        const initialWordCount = getWordCount(textContent);
        setWordCount(initialWordCount);

        // Set the initial content - truncation will be handled in the truncation effect
        setEditorContent(initialContent);
        handleSaveEvaluationData(data);

        // Set the article link
        if (data.articleLink) {
          setArticleLink(data.articleLink);
        }

        setFactCheckFindings(getNormalizedFactCheckFindings(data));
      }
    } catch (error) {
      dispatch(
        snackAdded({
          message: `Failed to load content`,
          type: "error",
        })
      );
      setFactCheckFindings([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveEvaluationData = async (data: any) => {
    // Process and validate the evaluationData
    try {
      let isValid = false;

      if (data.articleEEATScore) {
        try {
          // Get articles and analysis
          let articles = data.articleEEATScore.articles;
          let analysis = data.articleEEATScore.analysis;
          let suggestions = data.articleEEATScore.suggestions;

          // Parse if needed
          const parsedArticles =
            typeof articles === "string" ? JSON.parse(articles) : articles;
          const parsedAnalysis =
            typeof analysis === "string" ? JSON.parse(analysis) : analysis;

          // Check for required structure in articles
          if (
            !parsedArticles?.original?.scores ||
            !parsedArticles?.updated?.scores ||
            !parsedArticles?.original?.overall ||
            !parsedArticles?.updated?.overall
          ) {
            throw new Error("Articles data is missing required structure");
          }

          // Verify scores contain all required metrics
          const originalScores = parsedArticles.original.scores;
          const updatedScores = parsedArticles.updated.scores;

          if (
            typeof originalScores.experience !== "number" ||
            typeof originalScores.expertise !== "number" ||
            typeof originalScores.authoritativeness !== "number" ||
            typeof originalScores.trustworthiness !== "number"
          ) {
            throw new Error("Original scores are missing required metrics");
          }

          if (
            typeof updatedScores.experience !== "number" ||
            typeof updatedScores.expertise !== "number" ||
            typeof updatedScores.authoritativeness !== "number" ||
            typeof updatedScores.trustworthiness !== "number"
          ) {
            throw new Error("Updated scores are missing required metrics");
          }

          // Check analysis structure
          if (
            !parsedAnalysis?.experience?.comparison ||
            !parsedAnalysis?.expertise?.comparison ||
            !parsedAnalysis?.authoritativeness?.comparison ||
            !parsedAnalysis?.trustworthiness?.comparison ||
            !Array.isArray(parsedAnalysis?.summary)
          ) {
            throw new Error("Analysis is missing required structure");
          }

          // If we reach here without error, data is valid
          isValid = true;
        } catch (error) {
          console.error("Invalid EEAT score data:", error);
          isValid = false;
        }
      } else {
        isValid = true;
      }

      setEvaluationData({ articleEEATScore: data.articleEEATScore });
      setIsValidEvaluationData(isValid);
    } catch (error) {
      console.error("Failed to save evaluation data:", error);
    }
  };

  function convertToMarkdown(content: string) {
    // Add color span rule
    turndownService.addRule("colorSpan", {
      filter: function (node) {
        return node.nodeName === "SPAN" && node.style && node.style.color;
      },
      replacement: function (content, node) {
        const color = node.style.color;
        return `<span style="color: ${color};">${content}</span>`;
      },
    });

    return turndownService.turndown(content);
  }
  // Function to save content
  const handleSaveContent = async (content: string, options: any) => {
    // Mark that we are currently saving so other updates can be deferred
    isContentSaving.current = true;

    const markdownContent = convertToMarkdown(content);

    // Calculate character count from markdown content
    const charCount = markdownContent.length;

    try {
      const result = await saveContent(markdownContent, { ...options, charCount });

      // Update refs only (not state) to avoid triggering effects that reset cursor
      if (options.isHumanized) {
        humanizedHtmlRef.current = content;
      } else {
        htmlRef.current = content;
      }

      // Update the reference of the last successfully saved content
      lastSavedContent.current = content;

      return result;
    } catch (error) {
      dispatch(
        snackAdded({
          message: "Failed to save changes",
          type: "error",
        })
      );
      throw error;
    } finally {
      // Reset the saving flag regardless of success or failure
      isContentSaving.current = false;
    }
  };

  // Load content when component mounts or ID changes
  useEffect(() => {
    if (id) {
      loadContent();
    }
  }, [id]);

  // Handle content truncation for preview mode
  useEffect(() => {
    if (!editor || !hideArticle) {
      setUseScrollBasedTruncation(false);
      return;
    }

    // Wait for content to be loaded (use ref instead of state to avoid re-triggering on save)
    if (!isContentLoaded.current) {
      return;
    }

    const content = editor.getHTML();

    if (content.length < 100) {
      setUseScrollBasedTruncation(false);
      return;
    }

    // Use TipTap commands to insert unlock card after 4 paragraphs
    setUseScrollBasedTruncation(true);

    isProgrammaticChange.current = true;
    insertUnlockCardWithTipTap(editor, content, isOptimizedMode);
    isProgrammaticChange.current = false;
    setTruncatedContent(content);
  }, [hideArticle, editor]);


  // Helper functions
  const selectedWords = () => {
    return countSelectedWords(editor);
  };

  const handleAddMetaToArticle = (html: string) => {
    return addMetaToArticle(html, title, description);
  };

  const handleDownload = () => {
    if (hideArticle) {
      setShowPaymentModal(true);
      return;
    }

    let htmlToDownload = editor?.getHTML();
    const htmlWithMeta = handleAddMetaToArticle(htmlToDownload);
    Export2Docx(htmlWithMeta, title);

    // Reset download flag if using the optimized editor
    if (additionalProps.setIsDownloadContent) {
      additionalProps.setIsDownloadContent(false);
    }
  };

  const handleCopy = async () => {
    if (hideArticle) {
      setShowPaymentModal(true);
      return;
    }

    try {
      let htmlToCopy = editor?.getHTML();
      const htmlWithMeta = handleAddMetaToArticle(htmlToCopy);

      const success = await copyHtmlToClipboard(htmlWithMeta);
      if (success) {
        dispatch(
          snackAdded({
            message: "Content copied to clipboard!",
            type: "success",
            duration: 2000,
          })
        );
      } else {
        throw new Error("Clipboard copy failed");
      }
    } catch (err) {
      console.error("Failed to copy: ", err);
      dispatch(
        snackAdded({
          message: "Failed to copy content. Please try again.",
          type: "error",
          duration: 1000,
        })
      );
    }

    // Reset copy flag if using the optimized editor
    if (additionalProps.setIsCopyContent) {
      additionalProps.setIsCopyContent(false);
    }
  };

  const handleAIDropdown = () => {
    if (isEEATMode) {
      dispatch(
        snackAdded({
          message:
            "You can't apply AI to an EEAT article. Please use Update Article to improve your article.",
          type: "info",
        })
      );
      return;
    }
    isProgrammaticChange.current = true;
    const { from, to } = editor.state.selection;
    editor
      ?.chain()
      .setTextSelection({ from, to })
      .focus()
      .setHighlight("lightgray")
      .run();
    isProgrammaticChange.current = false;

    let startCoords = editor.view.coordsAtPos(from);
    let endCoords = editor.view.coordsAtPos(to);

    // Set coordinates for the dropdown
    // Position it slightly below and to the left of the selected text
    // Use the end of the selection (where the cursor would be) as the anchor point
    setCords({
      top: endCoords.bottom + 10, // 10px below the selection
      left: endCoords.left - 20, // 20px to the left of the end of selection
      bottom: endCoords.bottom,
      right: endCoords.right,
    });

    if (!scrollContainerRef.current) {
      return;
    }

    // Find all possible scroll sources and use the maximum value
    const editorDOM = editor.view.dom;

    // Get all possible scroll values
    const windowScrollY =
      window.scrollY ||
      window.pageYOffset ||
      document.documentElement.scrollTop ||
      0;
    const containerScrollTop = scrollContainerRef.current.scrollTop || 0;
    const editorScrollTop = editorDOM.scrollTop || 0;

    // Check for scrollable parent elements
    let parentScrollTop = 0;
    let currentElement = editorDOM.parentElement;
    let scrollableParent = null;

    while (currentElement && currentElement !== document.body) {
      if (
        currentElement.scrollHeight > currentElement.clientHeight &&
        currentElement.scrollTop > 0
      ) {
        scrollableParent = currentElement;
        parentScrollTop = currentElement.scrollTop;

        break;
      }
      currentElement = currentElement.parentElement;
    }

    // Use the maximum scroll value from any source
    const scrollY = Math.max(
      windowScrollY,
      containerScrollTop,
      editorScrollTop,
      parentScrollTop
    );

    // Store the effective scroll position for use in dropdown positioning
    const effectiveScrollPosition = scrollY;

    // Calculate position relative to the editor's position in the container
    const containerRect = scrollContainerRef.current.getBoundingClientRect();
    const editorRect = editor.view.dom.getBoundingClientRect();

    // We will position the dropdown at the beginning of the last line of selected text
    // Use a safer approach to get text content
    const selectionText = editor.state.doc.textBetween(from, to);

    // Find the last newline in the selection
    const lastNewlineIndex = selectionText.lastIndexOf("\n");

    // Position at the start of the last line
    let lastLineStartPos = from;
    if (lastNewlineIndex !== -1) {
      // Found a newline, position after it (first character of last line)
      lastLineStartPos = from + lastNewlineIndex + 1;
    }

    // Get coordinates for the start of the last line
    let startOfLastLineCoords = editor.view.coordsAtPos(lastLineStartPos);

    // Calculate available space below the selection
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - endCoords.bottom;

    // Height of the dropdown (approximate)
    const dropdownHeight = 150; // Adjust based on your dropdown's actual height

    // Check if there's enough space below
    const positionAbove = spaceBelow < dropdownHeight;

    let relativeTop, relativeLeft;

    if (positionAbove) {
      // Position above the selection
      relativeTop =
        startOfLastLineCoords.top -
        containerRect.top +
        scrollY -
        dropdownHeight;
      relativeLeft = startOfLastLineCoords.left - containerRect.left;
    } else {
      // Position below the selection (original behavior)
      relativeTop = startOfLastLineCoords.top - containerRect.top + scrollY;
      relativeLeft = startOfLastLineCoords.left - containerRect.left;
    }

    // Set coordinates for the dropdown
    setCords({
      top: positionAbove ? relativeTop : relativeTop + 10, // If above, no extra padding
      left: relativeLeft,
      bottom: relativeTop,
      right: endCoords.right - containerRect.left,
    });

    // Extract selected blocks
    const selectedBlocks = extractSelectedBlocks(
      editor,
      editor.state.selection.from,
      editor.state.selection.to,
      converter
    );

    if (selectedWords() > 500) {
      dispatch(
        snackAdded({
          message: "Please select less than 500 words",
          type: "error",
        })
      );
      editor?.commands.unsetHighlight();
      return;
    }

    // Set state for dropdown
    setShowAIDropdown(true);
    setShouldShowBubbleMenu(false);
    setSelectedText(selectedBlocks);

    // Focus and scroll to the element
    setTimeout(() => {
      try {
        // Try direct window scrolling as a backup
        const editorRect = scrollContainerRef.current
          ?.querySelector(".ProseMirror")
          ?.getBoundingClientRect();
        if (editorRect) {
          // Use a simpler approach - just scroll to a fixed offset from the selection
          // This is more reliable across different scroll positions
          const targetScroll = Math.max(0, relativeTop - 150);

          // Find the scrollable container
          let scrollContainer = null;
          let currentEl = editorDOM;

          while (currentEl && currentEl !== document.body) {
            if (currentEl.scrollHeight > currentEl.clientHeight) {
              scrollContainer = currentEl;
              break;
            }
            currentEl = currentEl.parentElement;
          }

          // If we found a scrollable container, scroll it
          if (scrollContainer) {
            scrollContainer.scrollTo({
              top: targetScroll,
              behavior: "smooth",
            });
          } else {
            window.scrollTo({
              top: targetScroll,
              behavior: "smooth",
            });
          }
        }
      } catch (error) {
        console.error("Error during focus/scroll:", error);
      }
    }, 100);
  };

  const handleCloseAIDropdown = () => {
    setShowAIDropdown(false);
    setShouldShowBubbleMenu(true);
    setSelectedText([]);
    setCords({
      top: 0,
      left: 0,
      bottom: 0,
      right: 0,
    });

    // Remove the highlight
    editor?.commands.unsetHighlight();
    setBottomMargin(0);
  };

  const handleSelectFactCheckFinding = useCallback((finding: FactCheckFinding) => {
    if (!editor) return;
    const range = findRangeForFinding(editor, finding);
    if (!range) {
      dispatch(
        snackAdded({
          message: "Could not find this text in the current editor version.",
          type: "warning",
        })
      );
      return;
    }

    editor
      .chain()
      .focus()
      .setTextSelection({ from: range.from, to: range.to })
      .run();

    // Smooth-scroll to the selected text (TipTap's scrollIntoView is a jump)
    requestAnimationFrame(() => {
      try {
        const { node } = editor.view.domAtPos(range.from);
        const el = node.nodeType === Node.TEXT_NODE ? node.parentElement : (node as HTMLElement);
        el?.scrollIntoView({ behavior: "smooth", block: "center" });
      } catch {}
    });
  }, [dispatch, editor]);

  const handleApplyFactCheckAction = useCallback((finding: FactCheckFinding) => {
    if (!editor) return;
    const range = findRangeForFinding(editor, finding);
    if (!range) {
      dispatch(
        snackAdded({
          message: "Could not locate this text to apply the action.",
          type: "warning",
        })
      );
      return;
    }

    const normalizedAction = String(finding.action || "").trim().toLowerCase();

    if (normalizedAction === "replace") {
      const replacement = finding.replacement_text?.trim();
      if (!replacement) {
        dispatch(
          snackAdded({
            message: "Replacement text is missing for this finding.",
            type: "warning",
          })
        );
        return;
      }

      // When the range spans different block nodes (paragraphs), inserting a
      // plain string would merge them. Use structured content to preserve the
      // paragraph boundary.
      const $from = editor.state.doc.resolve(range.from);
      const $to = editor.state.doc.resolve(range.to);
      const crossesBlock = $from.parent !== $to.parent;

      if (crossesBlock) {
        const parts = replacement.split("\n").filter((p: string) => p.trim());
        const content =
          parts.length > 1
            ? parts.map((text: string) => ({
                type: "paragraph",
                content: [{ type: "text", text: text.trim() }],
              }))
            : [
                { type: "paragraph", content: [{ type: "text", text: parts[0] || replacement }] },
                { type: "paragraph", content: [] },
              ];
        editor
          .chain()
          .focus()
          .insertContentAt({ from: range.from, to: range.to }, content)
          .run();
      } else {
        editor
          .chain()
          .focus()
          .insertContentAt({ from: range.from, to: range.to }, replacement)
          .run();
      }

      dispatch(
        snackAdded({
          message: "Replacement applied.",
          type: "success",
        })
      );

      // Keep highlight/underline after replacement by shifting the finding's
      // source text to the applied replacement text.
      setFactCheckFindings((prev) =>
        prev.map((item) =>
          item.id === finding.id
            ? {
                ...item,
                original_text: replacement,
                applied: true,
              }
            : item
        )
      );
    } else if (normalizedAction === "remove") {
      const $fromR = editor.state.doc.resolve(range.from);
      const $toR = editor.state.doc.resolve(range.to);
      const crossesBlockR = $fromR.parent !== $toR.parent;

      if (crossesBlockR) {
        // Preserve paragraph boundary by replacing with two empty paragraphs
        editor
          .chain()
          .focus()
          .insertContentAt(
            { from: range.from, to: range.to },
            [{ type: "paragraph", content: [] }, { type: "paragraph", content: [] }]
          )
          .run();
      } else {
        editor
          .chain()
          .focus()
          .insertContentAt({ from: range.from, to: range.to }, "")
          .run();
      }

      dispatch(
        snackAdded({
          message: "Text removed.",
          type: "success",
        })
      );

      setFactCheckFindings((prev) =>
        prev.map((item) =>
          item.id === finding.id
            ? {
                ...item,
                applied: true,
              }
            : item
        )
      );
    } else {
      dispatch(
        snackAdded({
          message: `Action "${finding.action}" is not directly appliable.`,
          type: "warning",
        })
      );
      return;
    }

    setActiveFindingId(finding.id);
    setTimeout(() => {
      if (!editor) return;
      const ranges = factCheckFindings
        .map((item) => findRangeForFinding(editor, item))
        .filter((item): item is FactCheckDecorationRange => !!item);
      (editor.commands as any).setFactCheckRanges?.(ranges);
    }, 50);
  }, [dispatch, editor, factCheckFindings]);

  const applyFactCheckDecorations = useCallback(() => {
    if (!editor) return;
    if (factCheckFindings.length === 0) {
      (editor.commands as any).clearFactCheckRanges?.();
      return;
    }

    const results = factCheckFindings.map((finding) => findRangeForFinding(editor, finding));
    const ranges = results.filter((range): range is FactCheckDecorationRange => !!range);
    const matched = ranges.length;
    const unmatched = factCheckFindings.length - matched;
    if (factCheckFindings.length > 0) {
      console.log(`[FactCheck] Matched ${matched}/${factCheckFindings.length} findings (${unmatched} unmatched)`);
    }

    (editor.commands as any).setFactCheckRanges?.(ranges);
  }, [editor, factCheckFindings]);

  const unlockArticle = () => {
    setShowPaymentModal(true);

    // Reset flags if using the regular editor
    if (!isOptimizedMode) {
      if (additionalProps.setIsDownloadContent) {
        additionalProps.setIsDownloadContent(false);
      }
      if (additionalProps.setIsCopyContent) {
        additionalProps.setIsCopyContent(false);
      }
    }
  };

  // Effect to handle unlock events from the custom TipTap extension
  useEffect(() => {
    if (!hideArticle || !useScrollBasedTruncation) {
      return;
    }

    const handleUnlockEvent = (event: CustomEvent) => {
      event.preventDefault();
      unlockArticle();
    };

    // Listen for the custom unlock event
    document.addEventListener('unlockArticle', handleUnlockEvent as EventListener);

    return () => {
      document.removeEventListener('unlockArticle', handleUnlockEvent as EventListener);
    };
  }, [hideArticle, useScrollBasedTruncation]);

  // Clean up editor on unmount
  useEffect(() => {
    return () => {
      if (editor) {
        editor.destroy();
      }
    };
  }, [editor]);

  // Effect for copy/download in regular editor mode
  useEffect(() => {
    if (additionalProps.isCopyContent) {
      handleCopy();
    }
  }, [additionalProps.isCopyContent]);

  useEffect(() => {
    if (additionalProps.isDownloadContent) {
      handleDownload();
    }
  }, [additionalProps.isDownloadContent]);

  // Effect to handle content switching (only on toggle, NOT on save)
  useEffect(() => {
    if (!editor || !isContentLoaded.current) return;

    const switchContent = async () => {
      try {
        // Capture sequence number for this invocation (Fix P1)
        const mySequence = ++toggleSequence.current;

        // Flush CURRENT channel before switching (Fix #3)
        // This ensures any pending save for the current view completes first
        const currentChannel = isEditingHumanizedContent.current ? 'humanized' : 'original';

        // Block toggle on flush error to prevent data loss
        try {
          await flushPendingSaves(currentChannel);
        } catch (error) {
          console.error('[TOGGLE] Save failed during flush, aborting toggle:', error);
          // Revert toggle UI to match the channel we're still editing
          dispatch(setShowHumanizeContent(isEditingHumanizedContent.current));
          dispatch(snackAdded({
            message: "Failed to save changes. Please try toggling again.",
            type: "error",
          }));
          return;
        }

        // Check if a newer toggle was initiated while we were flushing (Fix P1)
        if (mySequence !== toggleSequence.current) {
          return; // Abort silently - newer toggle will handle the switch
        }

        // Read from refs (always latest saved content) instead of state
        const newContent = additionalProps.showHumanizeContent
          ? humanizedHtmlRef.current
          : htmlRef.current;
        const currentContent = editor.getHTML();

        if (newContent !== currentContent) {
          isProgrammaticChange.current = true;

          try {
            // Update tracking ref for new channel
            isEditingHumanizedContent.current = additionalProps.showHumanizeContent;
            setIsEditingHumanizedVersion(additionalProps.showHumanizeContent);

            // Update content
            if (hideArticle && useScrollBasedTruncation) {
              insertUnlockCardWithTipTap(editor, newContent, isOptimizedMode);
            } else {
              editor.commands.setContent(newContent);
            }
          } finally {
            // Clear suppression even if setContent throws (Fix P2)
            // Ensures flag never gets stuck, which would block all future user edits
            isProgrammaticChange.current = false;
          }

          // Update word count after content is rendered
          setTimeout(() => {
            if (editor) {
              setWordCount(getWordCount(editor.getText()));
            }
            applyFactCheckDecorations();
          }, 100);
        }
      } catch (error) {
        // Catch any unhandled errors in switchContent to prevent unhandled rejection (Fix P2)
        console.error('[TOGGLE] Error during content switch:', error);
      }
    };

    switchContent();
  }, [
    additionalProps.showHumanizeContent,
    editor,
    hideArticle,
    useScrollBasedTruncation,
    isOptimizedMode,
    flushPendingSaves,
    applyFactCheckDecorations,
  ]);

  useEffect(() => {
    applyFactCheckDecorations();
  }, [applyFactCheckDecorations, additionalProps.showHumanizeContent]);

  // Click-to-card: when user clicks on a fact-check underline in the editor,
  // set the active finding so the sidebar card scrolls into view.
  useEffect(() => {
    if (!editor) return;
    const editorEl = editor.view.dom;
    const handleFactCheckClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const decorated = target.closest("[data-fact-check-id]") as HTMLElement | null;
      if (decorated) {
        const id = Number(decorated.getAttribute("data-fact-check-id"));
        if (!isNaN(id) && id) setActiveFindingId(id);
      }
    };
    editorEl.addEventListener("click", handleFactCheckClick);
    return () => editorEl.removeEventListener("click", handleFactCheckClick);
  }, [editor]);

  // Cleanup effect: flush pending saves on unmount (Fix #5 - save-on-unmount)
  useEffect(() => {
    return () => {
      // Flush pending saves on unmount to prevent data loss
      // This is fire-and-forget since unmount cleanup can't be async
      // Catch rejection to prevent unhandled promise rejection (Fix P1)
      flushPendingSaves().catch((error) => {
        console.warn('[UNMOUNT] Failed to flush pending saves:', error);
      });
    };
  }, [flushPendingSaves]);

  // Add a new effect to track isEditingHumanizedVersion changes separately
  useEffect(() => {}, [isEditingHumanizedVersion]);

  // Add this effect to handle humanization
  useEffect(() => {
    const handleHumanization = async () => {
      // Exit early if not humanizing, no editor, or no humanize function
      if (
        !additionalProps.isHumanizing ||
        !editor ||
        !additionalProps.onHumanize
      )
        return;

      try {
        const content = editor.getHTML();

        // Call the provided humanize function
        // local
        //const url = "http://localhost:8000/api/v1/humanizer/stream";
        // prod
        const url = "https://humanizer.seowind.io/api/v1/humanizer/stream";
        const response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            text: content,
            source: "ai_writing",
          }),
        });

        if (response.status === 429) {
          const data = await response.json();
          dispatch(
            snackAdded({
              message: data.message,
              type: "error",
              duration: 2000,
            })
          );
          dispatch(endHumanization(false));
          return;
        }

        const reader = response.body?.getReader();
        const decoder = new TextDecoder("utf-8");
        let accumulatedText = "";

        while (true) {
          const { value, done } = await reader?.read()!;
          if (done) break;

          const chunk = decoder.decode(value, { stream: true });
          accumulatedText += chunk;

          const html = converter.makeHtml(accumulatedText);
          isProgrammaticChange.current = true;

          // Apply truncation if article should be hidden
          if (hideArticle && useScrollBasedTruncation) {
            insertUnlockCardWithTipTap(editor, html, isOptimizedMode);
          } else {
            editor.commands.setContent(html);
          }

          isProgrammaticChange.current = false;
        }
        // Save content and update state
        await handleSaveContent(editor.getHTML(), {
          isHumanized: true,
          isEdited: false,
        });

        // Update states in correct order
        dispatch(endHumanization(true));

        // Fetch latest content
        await loadContent();

        // Content will be updated through the normal fetch mechanism
      } catch (error) {
        console.error("Error humanizing content:", error);
        dispatch(
          snackAdded({
            message: "Failed to humanize content",
            type: "error",
            duration: 2000,
          })
        );
      }
    };

    handleHumanization();
  }, [additionalProps.isHumanizing, editor, additionalProps.onHumanize]);

  // Add this effect to handle navigation blocking during humanization
  useEffect(() => {
    // Block navigation while humanizing
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (additionalProps.isHumanizing) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    // Block Next.js client-side navigation
    const handleRouteChange = (url: string) => {
      if (additionalProps.isHumanizing) {
        // Prevent the default behavior
        window.history.pushState(null, "", window.location.href);
        // Show warning
        dispatch(
          snackAdded({
            message: "Please wait for humanization to complete",
            type: "warning",
            duration: 3000,
          })
        );
        throw "Navigation cancelled";
      }
    };

    // Handle popstate (browser back/forward buttons)
    const handlePopState = () => {
      if (additionalProps.isHumanizing) {
        // Push current URL back to history to prevent navigation
        window.history.pushState(null, "", window.location.href);
        dispatch(
          snackAdded({
            message: "Please wait for humanization to complete",
            type: "warning",
            duration: 3000,
          })
        );
      }
    };

    // Add event listeners
    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("popstate", handlePopState);
    router.events.on("routeChangeStart", handleRouteChange);

    // Cleanup
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("popstate", handlePopState);
      router.events.off("routeChangeStart", handleRouteChange);
    };
  }, [additionalProps.isHumanizing]);

  // Add effect to log when showHumanizeContent changes
  useEffect(() => {}, [additionalProps.showHumanizeContent]);

  const handleToggle = (event) => {
    const isChecked = event.target.checked;
    // Just update the display preference
    dispatch(setShowHumanizeContent(isChecked));
  };

  const checArticleLimit = async () => {
    // Refresh the current plan
    const currentPlan = (await dispatch(getActivePlan())).payload?.plan;
    const numberOfAIWords = Number(currentPlan?.numberOfWordsInPlan || 0);
    const numberOfArticles = Number(currentPlan?.numberOfArticlesInPlan || 0);
    let aiWordsLeft = numberOfAIWords - (currentPlan?.totalWordsWritten || 0);
    let articlesLeft =
      numberOfArticles - (currentPlan?.totalArticlesWritten || 0);
    if (aiWordsLeft < 0) {
      aiWordsLeft = 0;
    }
    if (articlesLeft < 0) {
      articlesLeft = 0;
    }

    if (aiWordsLeft === 0 || articlesLeft === 0) {
      let text = "";
      if (articlesLeft === 0) {
        text = `You've reached a limit for Updating Articles`;
      } else {
        text = ` You've reached a limit of word count`;
      }

      dispatch(aiWordsUpgradeModalShowed(text));
      return true;
    }
    return false;
  };
  const handleUpdateArticleEEAT = async () => {
    setIsOptimizeContentDialogLoading(true);
    setIsOptimizeContentDialogOpen(false);
    const planDetails = (await dispatch(getActivePlan())) as any;
    let plan = planDetails?.payload?.plan ?? undefined;

    if (
      plan === undefined ||
      Object.keys(plan).length === 0 ||
      planDetails?.payload?.error
    ) {
      setShowOnboardingModal(true);
      setIsOptimizeContentDialogLoading(false);
      return;
    }

    const isArticleLimitReached = await checArticleLimit();

    setIsOptimizeContentDialogLoading(false);
    if (!isArticleLimitReached) {
      setIsOptimizeContentDialogOpen(true);
    }
  };
  const handleCloseOptimizeContentDialog = () => {
    setIsOptimizeContentDialogOpen(false);
  };
  const handleOptimizeContentDialogOpen = () => {
    setIsOptimizeContentDialogOpen(true);
  };
  return (
    <>
      <Head>
        <title>{title}</title>
      </Head>
      <AddPaymentModal
        open={showPaymentModal}
        onClose={() => {
          setShowPaymentModal(false);

          // Reset flags if using the regular editor
          if (!isOptimizedMode) {
            if (additionalProps.setIsDownloadContent) {
              additionalProps.setIsDownloadContent(false);
            }
            if (additionalProps.setIsCopyContent) {
              additionalProps.setIsCopyContent(false);
            }
          }
        }}
        location={""}
      />
      {showOnboardingModal && (
        <Onboarding
          onClose={() => setShowOnboardingModal(false)}
          isEEATMode={isEEATMode}
          articleLink={articleLink}
        />
      )}
      <OptimizeContentDialog
        open={isOptimizeContentDialogOpen}
        limit={false}
        onClose={handleCloseOptimizeContentDialog}
        onCreated={handleOptimizeContentDialogOpen}
        isRedirect={false}
        isShowInputs={false}
        link={articleLink}
      />
      <Box
        sx={{
          width: "100%",
          margin: "auto",
          position: "relative",
          display: "flex",
          gap: 3,
          px: 3,
          height: "calc(100vh - 64px)",
          overflow: "hidden",
        }}
      >
        <Box
          ref={scrollContainerRef}
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            alignItems: "center",
            height: "100%",
            overflow: "auto",
            position: "relative",
          }}
        >
          <Box
            data-testid="article-editor-toolbar-shell"
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              position: "sticky",
              top: 0,
              zIndex: hideArticle || isEEATMode ? 1 : 20,
            }}
          >
            <Box
              sx={{
                width: "100%",
                maxWidth: isWritingMode ? "1180px" : "1000px",
                backgroundColor: "#fff",
                borderBottom: "1px solid #EDEFF5",
                boxShadow: "0 2px 10px rgba(18, 24, 40, 0.05)",
              }}
            >
              <MenuBar
                editor={editor}
                handleAidropdown={handleAIDropdown}
                evaluationData={evaluationData}
                isEEATMode={isEEATMode}
                isWritingMode={isWritingMode}
                onToggleWritingMode={() =>
                  setIsWritingMode((prevMode) => !prevMode)
                }
              />
            </Box>
          </Box>
          <Card
            sx={{
              width: "100%",
              maxWidth: isWritingMode ? "1180px" : "1000px",
              mt: "12px",
              display: "flex",
              flexDirection: "column",
              position: "relative",
              flex: 1,
              ...(additionalProps.isHumanizing && {
                minHeight: "calc(100vh - 64px)",
              }),
            }}
          >
            <Editor
              editor={editor}
              isHumanizer={false}
              isLoading={isLoading}
              title={title}
              description={description}
              wordCount={wordCount}
            />

            {/* Render skeletons below the Editor for hiding content - only when not using scroll-based truncation */}
            {hideArticle && !useScrollBasedTruncation && (
              <Box
                sx={{
                  width: "100%",
                  padding: "16px",
                  background: "linear-gradient(to bottom, transparent, white)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                {/* Skeleton Loaders */}
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                  }}
                >
                  <Skeleton
                    variant="text"
                    height={20}
                    width={"100%"}
                    animation={false}
                  />
                  <Skeleton
                    variant="text"
                    height={20}
                    width={"100%"}
                    animation={false}
                  />
                  <Skeleton
                    variant="text"
                    height={20}
                    width={"100%"}
                    animation={false}
                  />
                  <Skeleton
                    variant="text"
                    height={20}
                    width={"100%"}
                    animation={false}
                  />
                  <Skeleton
                    variant="text"
                    height={15}
                    width={"65%"}
                    animation={false}
                  />
                </Box>

                {/* Unlock Card */}
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "65px",
                  }}
                >
                  <Card
                    sx={{
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      backgroundColor: "#5048E5",
                      borderRadius: "10px",
                      boxShadow: "0 0 10px 0 rgba(0, 0, 0, 0.1)",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        height: "100%",
                        width: "100%",
                        justifyContent: "space-between",
                        padding: "10px 16px",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-start",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "14px",
                            fontFamily: "Inter",
                            fontWeight: 600,
                            color: "#fff",
                          }}
                        >
                          Your {isOptimizedMode ? "optimized " : ""}content is
                          ready
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "16px",
                            fontFamily: "Inter",
                            fontWeight: 600,
                            color: "#fff",
                          }}
                        >
                          Subscribe to see the full article
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <Button
                          variant="contained"
                          color="primary"
                          size="large"
                          onClick={unlockArticle}
                          sx={{
                            fontSize: "14px",
                            fontFamily: "Inter",
                            fontWeight: 600,
                            color: "#fff",
                            padding: "8px 16px",
                            background: "#EB1F8D !important",
                            width: "150px",
                            height: "49px",
                            borderRadius: "8px",
                          }}
                        >
                          Unlock Now
                        </Button>
                      </Box>
                    </Box>
                  </Card>
                </Box>
              </Box>
            )}

            <BubbleMenu
              editor={editor}
              className="bubble-menu"
              tippyOptions={{
                duration: 100,
              }}
            >
              <Button
                variant="contained"
                sx={{
                  width: "120px",
                  height: "40px",
                  padding: "8px 22px",
                  fontSize: "14px",
                  fontWeight: 400,
                }}
                onClick={handleAIDropdown}
              >
                <MagicWandIcon sx={{ mt: 1 }} />
                Ask AI
              </Button>
            </BubbleMenu>

            {showAIDropdown && (
              <RegenerateDropdown
                startCord={cords.top}
                endCord={cords.left}
                bottomCord={cords.bottom}
                rightCord={cords.right}
                open={showAIDropdown}
                selectedText={selectedText}
                editor={editor}
                onClose={handleCloseAIDropdown}
                setMarginBottom={(margin) => setBottomMargin(margin)}
                scrollContainerRef={scrollContainerRef}
              />
            )}
          </Card>
        </Box>
        {/* Sidebar */}

        <Box
          sx={{
            width: "384px",
            height: "10%",
            display: isWritingMode ? "none" : "block",
            position: "relative",
            zIndex: 3,
          }}
        >
          <Sidebar
            title={
              isOptimizedMode ? "EEAT Score Comparison" : "Article EEAT Score"
            }
            recommendedScore={8.0}
            evaluationData={evaluationData}
            optimizedMode={isOptimizedMode}
            isLoading={isLoading}
            isEEATMode={isEEATMode}
            onUpdateArticleEEAT={handleUpdateArticleEEAT}
            isEEATLoading={isOptimizeContentDialogLoading}
            factCheckFindings={factCheckFindings}
            onSelectFactCheckFinding={handleSelectFactCheckFinding}

            onApplyFactCheckAction={handleApplyFactCheckAction}

            activeFindingId={activeFindingId}
          />
        </Box>
      </Box>
    </>
  );
};

export default ArticleEditorComponent;
